import * as React from 'react';
import { useContext, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import InlineMessage, { InlineMessageState } from '../InlineMessage';

interface RecoveryCodesDisplayProps {
  /**
   * the clear text codes. they exist exactly once, in this render - the backend
   * only stores their hashes
   */
  codes: string[];
  /**
   * unique per usage, ends up in the id of the print container and in the
   * message region
   */
  section: string;
  /**
   * closes the block. the parent decides what "understood" means for it
   */
  onDismiss: () => void;
}

/**
 * copyToClipboard
 *
 * navigator.clipboard only exists in a secure context and only reacts to a real
 * user gesture; the textarea fallback is what every browser understood before
 * it. both paths are tried before the user is told that copying failed, because
 * the codes are gone after this screen and "copy did not work" must never be a
 * silent failure.
 *
 * @param text
 */
async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);

      return true;
    } catch (error) {
      /**
       * falls through to the legacy path on purpose - a denied permission is not
       * a reason to give up yet
       */
    }
  }

  try {
    const area = document.createElement('textarea');

    area.value = text;
    /**
     * off screen instead of display:none - a hidden element cannot be selected
     */
    area.setAttribute('readonly', 'readonly');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();

    const copied = document.execCommand('copy');

    document.body.removeChild(area);

    return copied;
  } catch (error) {
    return false;
  }
}

/**
 * RecoveryCodesDisplay
 *
 * the one and only moment the recovery codes are readable. it is shown after
 * confirming the authenticator app and after regenerating, and it carries the
 * warning, the list, and the two ways out of the screen that do not lose the
 * codes: copy and print.
 */
const RecoveryCodesDisplay: React.FC<RecoveryCodesDisplayProps> = ({ codes, section, onDismiss }) => {
  const { I18N } = useContext(AppContext);
  const [ message, setMessage ] = useState<InlineMessageState | null>(null);

  /**
   * handleCopy
   */
  const handleCopy = async () => {
    const copied = await copyToClipboard(codes.join('\n'));

    setMessage(copied
      ? { kind: 'success', text: I18N.t("Die Wiederherstellungscodes wurden in die Zwischenablage kopiert.") }
      : { kind: 'error', text: I18N.t("Die Wiederherstellungscodes konnten nicht kopiert werden. Bitte notieren Sie sie von Hand.") });
  };

  /**
   * handlePrint
   *
   * the attribute on <html> is what the print stylesheet keys on, so only the
   * code list ends up on the paper instead of the whole application shell. it is
   * removed again right after the (synchronous) print dialog returns, and in a
   * finally so an aborted print cannot leave the app in print mode
   */
  const handlePrint = () => {
    const root = document.documentElement;

    try {
      root.setAttribute('data-printing-recovery-codes', 'true');
      window.print();
    } finally {
      root.removeAttribute('data-printing-recovery-codes');
    }
  };

  return (
    <div data-recovery-codes role="group" aria-labelledby={ 'recovery-codes-heading-' + section }>
      <h3 id={ 'recovery-codes-heading-' + section } data-recovery-codes-heading>
        { I18N.t("Ihre Wiederherstellungscodes") }
      </h3>
      { /* role="alert" and not a plain paragraph: this is the one warning the
           user must not scroll past, and it appears after an action */ }
      <p data-recovery-codes-warning role="alert">
        { I18N.t("Diese Codes werden nur jetzt angezeigt und lassen sich später nicht erneut aufrufen. Sichern Sie sie jetzt an einem sicheren Ort. Jeder Code lässt sich genau einmal verwenden.") }
      </p>
      <ol data-recovery-codes-list>
        { codes.map((code) => (
          <li key={ code } data-recovery-code>{ code }</li>
        )) }
      </ol>
      <div data-recovery-codes-actions>
        <button type="button" data-secondary data-small onClick={ handleCopy }>
          { I18N.t("Codes kopieren") }
        </button>
        <button type="button" data-secondary data-small onClick={ handlePrint }>
          { I18N.t("Codes drucken") }
        </button>
        <button type="button" data-primary-dark data-small onClick={ onDismiss }>
          { I18N.t("Ich habe die Codes gesichert") }
        </button>
      </div>
      <InlineMessage message={ message } section={ 'recovery-codes-' + section } />
    </div>
  );
};

export default RecoveryCodesDisplay;
